import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import {
  AUTH_PARAMS as USIP_QUERY_PARAMS,
  EXTERNAL_APPS as USIP_APPLICATIONS,
} from 'platform/user/authentication/constants';
import { externalApplicationsConfig } from 'platform/user/authentication/usip-config';
import {
  API_SIGN_IN_SERVICE_URL as SIS_API_URL,
  OAUTH_KEYS as SIS_QUERY_PARAM_KEYS,
} from '~/platform/utilities/oauth/constants';
import { HOME_LABEL } from './poaRequests';

const ARP_SIGN_IN_URL = '/representative/sign-in';
const USIP_BASE_URL = environment.BASE_URL;
export const SUBMISSIONS_PATH = '/representative/submissions';
export const SUBMIT_FORMS_PATH = '/representative/submit-forms-and-files';

export const getSignInUrl = ({ returnUrl } = {}) => {
  const signInPath = ARP_SIGN_IN_URL;
  const url = new URL(signInPath, USIP_BASE_URL);
  url.searchParams.set(USIP_QUERY_PARAMS.application, USIP_APPLICATIONS.ARP);
  url.searchParams.set(USIP_QUERY_PARAMS.OAuth, true);
  if (returnUrl) {
    url.searchParams.set(USIP_QUERY_PARAMS.to, returnUrl);
  }
  return url;
};

export const SIGN_OUT_URL = (() => {
  const url = new URL(SIS_API_URL({ endpoint: 'logout' }));
  url.searchParams.set(
    SIS_QUERY_PARAM_KEYS.CLIENT_ID,
    externalApplicationsConfig[USIP_APPLICATIONS.ARP].oAuthOptions.clientId,
  );

  return url;
})();

// URL of the 21a "Apply for accreditation" form introduction page.
export const ACCREDITATION_21A_INTRO_URL =
  '/representative/accreditation/attorney-claims-agent-form-21a/introduction';

// OGC accreditation, discipline, and fees program page.
export const OGC_ACCREDITATION_URL = 'https://www.va.gov/ogc/accreditation.asp';

// A claimant ID goes into an API path. Even encoded, `..` climbs a level, so
// only the characters an id can have are allowed. The loaders check it before
// any request, and `utilities/api.js` refuses an id that fails it.
export const CLAIMANT_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const SEARCH_PARAMS = {
  STATUS: 'status',
  SORT: 'sort',
  SIZE: 'perPage',
  NUMBER: 'page',
  SELECTED_INDIVIDUAL: 'show',
};
export const SORT_BY = {
  CREATED: 'created_at',
  RESOLVED: 'resolved_at',
  OLDEST: 'oldest',
  NEWEST: 'newest',
};

export const STATUSES = {
  PENDING: 'pending',
  PROCESSED: 'processed',
};

export const PROCESSED_SORT_DEFAULTS = {
  SORT: 'newest',
  // default is 20 per page
  SIZE: '20',
  // default is page 1
  NUMBER: '1',
  SELECTED_INDIVIDUAL: 'all',
};

export const PENDING_SORT_DEFAULTS = {
  SORT: 'newest',
  // default is 20 per page
  SIZE: '20',
  // default is page 1
  NUMBER: '1',
  SELECTED_INDIVIDUAL: 'all',
};

export const SUBMISSION_DEFAULTS = {
  STATUS: null,
  SORT: 'newest',
  // default is 20 per page
  SIZE: '20',
  // default is page 1
  NUMBER: '1',
};

export const SORT_OPTIONS = {
  DESC_OPTION: 'Submitted date (newest)',
  ASC_OPTION: 'Submitted date (oldest)',
};

export const SORT_DEFAULTS = {
  SORT_BY: 'created_at',
  SORT_ORDER: 'desc',
  // default is 20 per page
  SIZE: 20,
  // default is page 1
  NUMBER: 1,
  SELECTED_INDIVIDUAL: 'all',
};

/**
 * Rows per page of the combined submission history, which pages in the
 * browser. Server paging (va.gov-team#159536) must request this size, not
 * `SUBMISSION_DEFAULTS.SIZE`.
 *
 * @type {number}
 */
export const COMBINED_HISTORY_PAGE_SIZE = 10;

/**
 * The combined history's URL parameters. `page`, `sort`, and `status` are
 * the server-paged list's names, so an old link still opens on its page.
 * The search text and the confirmation number filter are not among them:
 * both are free text a reader could put a name or an identifier into, and
 * the URL reaches history, analytics, logs, and shared links.
 *
 * @type {Readonly<{ PAGE: 'page', SORT: 'sort', DIR: 'dir', STATUS: 'status', FORM: 'form', DATE: 'date' }>}
 */
export const COMBINED_HISTORY_PARAMS = Object.freeze({
  PAGE: SEARCH_PARAMS.NUMBER,
  SORT: SEARCH_PARAMS.SORT,
  DIR: 'dir',
  STATUS: SEARCH_PARAMS.STATUS,
  FORM: 'form',
  DATE: 'date',
});

/**
 * The `form` filter value that keeps only QuickSubmit uploads, which have
 * files rather than a form number.
 *
 * @type {string}
 */
export const COMBINED_HISTORY_FILES_FILTER = 'files';

/**
 * The `status` filter's values, in the order the select offers them: the
 * VBMS statuses whose labels the rows show. `awaiting_receipt_warning`
 * shares "Processing error" with `processing_error`, so it is not offered
 * on its own.
 *
 * @type {readonly string[]}
 */
export const COMBINED_HISTORY_STATUS_FILTERS = Object.freeze([
  'received',
  'awaiting_receipt',
  'processing_error',
]);

/**
 * The combined history's columns, in table order: the `sort` parameter's
 * name for each, its header, what the summary line says the rows are
 * sorted by in each direction, and what the card view's sort select calls
 * that order. The select's entries are worded as the portal's other sort
 * selects are, "Newest" and "Oldest" rather than a column with a direction.
 * The Submission column stacks the date over the type and sorts by the
 * date. The table's headers, the summary, and the select read this, so a
 * column is named in one place.
 *
 * @type {readonly { key: string, label: string, sortedBy: { asc: string, desc: string }, sortOption: { asc: string, desc: string } }[]}
 */
export const COMBINED_HISTORY_COLUMNS = Object.freeze([
  {
    key: 'date',
    label: 'Submission',
    sortedBy: {
      asc: 'Submitted date (oldest first)',
      desc: 'Submitted date (newest first)',
    },
    sortOption: { asc: 'Oldest', desc: 'Newest' },
  },
  {
    key: 'claimant',
    label: 'Claimant',
    sortedBy: { asc: 'Claimant (A to Z)', desc: 'Claimant (Z to A)' },
    sortOption: { asc: 'Claimant A to Z', desc: 'Claimant Z to A' },
  },
  {
    key: 'status',
    label: 'Status',
    sortedBy: { asc: 'Status (A to Z)', desc: 'Status (Z to A)' },
    sortOption: { asc: 'Status A to Z', desc: 'Status Z to A' },
  },
  {
    key: 'confirmation',
    label: 'Confirmation #',
    sortedBy: {
      asc: 'Confirmation # (ascending)',
      desc: 'Confirmation # (descending)',
    },
    sortOption: {
      asc: 'Confirmation number, lowest first',
      desc: 'Confirmation number, highest first',
    },
  },
  {
    key: 'files',
    label: 'Files / Form',
    sortedBy: {
      asc: 'Files / Form (ascending)',
      desc: 'Files / Form (descending)',
    },
    sortOption: { asc: 'Files / Form A to Z', desc: 'Files / Form Z to A' },
  },
]);

/**
 * The `sort` parameter's values, in table order.
 *
 * @type {readonly string[]}
 */
export const COMBINED_HISTORY_SORT_KEYS = Object.freeze(
  COMBINED_HISTORY_COLUMNS.map(column => column.key),
);

/**
 * The `dir` parameter's values.
 *
 * @type {Readonly<{ ASC: 'asc', DESC: 'desc' }>}
 */
export const COMBINED_HISTORY_DIRECTIONS = Object.freeze({
  ASC: 'asc',
  DESC: 'desc',
});

export const DETAILS_BC_LABEL = 'details breadcrumb';
export const SUBMISSIONS_BC_LABEL = 'submissions breadcrumb';
export const submissionsBC = [
  {
    href: '/representative',
    label: HOME_LABEL,
  },
  {
    href: window.location.href,
    label: 'Submissions',
  },
];

export const INTENT_TO_FILE_FORM = '21-0966';
